/**
 * CREATE EXPAND ROW FORMATTER UTILITY
 * 
 * Creates a Tabulator cell formatter that renders clickable expand/collapse buttons
 * for rows that support toggle functionality. This formatter is used in the main
 * AssetTable to provide expand buttons when external data sources are linked.
 * 
 * FUNCTIONALITY:
 * - Creates clickable triangle buttons (right-pointing when collapsed, down-pointing when expanded)
 * - Integrates with global window.assetDataProps to access toggle functions
 * - Supports both Excel and Airtable data sources based on selectedAssetMode
 * - Provides visual feedback by changing button appearance on click
 * - Includes comprehensive error handling and logging
 * 
 * INTEGRATION:
 * - Used by createTableColumns when includeExpandColumn is true
 * - Relies on global data exposure from AssetTable
 * - Works with useLinkedData hook functions exposed globally
 * - Provides fallback behavior when toggle functions are not available
 * 
 * VISUAL DESIGN:
 * - Uses Unicode triangle characters for expand/collapse states
 * - Styled as borderless button with pointer cursor
 * - Consistent sizing and appearance across different table contexts
 * 
 * @returns {Function} Tabulator cell formatter function
 */
const createExpandRowFormatter = () => {
    return (cell, formatterParams) => {
        const button = document.createElement("button");
        button.innerHTML = "&#128898;"; // Right-pointing triangle character
        button.style.background = "none";
        button.style.border = "none";
        button.style.cursor = "pointer";
        button.style.fontSize = "12px";
        // button.style.padding = "2px";
        button.setAttribute("data-expanded", "false");

        /**
         * BUTTON CLICK HANDLER - TOGGLE ROW FUNCTIONALITY
         * 
         * Handles click events on expand buttons to toggle row expansion.
         * This function coordinates between the visual button state and
         * the actual data fetching/display functionality.
         * 
         * PROCESS:
         * 1. Safely accesses global data with fallbacks
         * 2. Determines which data source to use based on asset mode
         * 3. Updates button visual state (triangle direction)
         * 4. Calls appropriate toggle function for data source
         * 5. Provides comprehensive logging for debugging
         * 
         * ERROR HANDLING:
         * - Gracefully handles missing global data
         * - Provides fallback behavior when toggle functions unavailable
         * - Logs detailed information for troubleshooting
         * 
         * VISUAL FEEDBACK:
         * - Changes triangle direction to indicate expansion state
         * - Maintains consistent button appearance across interactions
         */
        button.onclick = () => {
            // Safely access global data with fallbacks
            const assetDataProps = window.assetDataProps || {};
            const selectedAssetMode = assetDataProps.selectedAssetMode;
            const excelRows = assetDataProps.excelRows || [];
            const selectedExcelColumn = assetDataProps.selectedExcelColumn;
            const toggleRowExcel = assetDataProps.toggleRowExcel;
            const toggleRowAirtable = assetDataProps.toggleRowAirtable;

            console.log('Expand button clicked - selectedAssetMode:', selectedAssetMode);
            console.log('Excel options available:', {
                excelRowsCount: excelRows.length,
                selectedExcelColumn: selectedExcelColumn,
                hasToggleExcel: !!toggleRowExcel,
                hasToggleAirtable: !!toggleRowAirtable
            });

            const isExpanded = button.getAttribute("data-expanded") === "true";
            if (isExpanded) {
                button.innerHTML = "&#128898;"; // Right-pointing triangle
                button.setAttribute("data-expanded", "false");
            } else {
                button.innerHTML = "&#128899;"; // Down-pointing triangle
                button.setAttribute("data-expanded", "true");
            }
            
            const row = cell.getRow();
            if (selectedAssetMode === 'link2Excel' && toggleRowExcel) {
                console.log('Calling toggleRowExcel with row:', row.getData());
                toggleRowExcel(row);
            } else if (selectedAssetMode === 'link2AirTable' && toggleRowAirtable) {
                console.log('Calling toggleRowAirtable with row:', row.getData());
                toggleRowAirtable(row);
            } else {
                console.log('No matching asset mode for toggle or missing toggle function');
                console.log('Available modes:', { selectedAssetMode, hasExcel: !!toggleRowExcel, hasAirtable: !!toggleRowAirtable });
            }
        };

        return button;
    };
};

export default createExpandRowFormatter;